import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactTooltip from "react-tooltip";

import { collectExecutiveSummaryRowKeys } from "./executiveSummaryUtils";

const EXEC_SUM_BULK_EXPAND_BANDS_TIP = "Expand all bands";
const EXEC_SUM_BULK_COLLAPSE_BANDS_TIP = "Collapse all bands";
const EXEC_SUM_BULK_EXPAND_BATCH = 3;
const EXEC_SUM_BULK_EXPAND_LOADER_MIN = 4;

function renderExpandCollapseIcon(collapsing) {
    return (
        <span
            className={`exec-sum-toolbar-btn__box-icon${
                collapsing ? " exec-sum-toolbar-btn__box-icon--minus" : " exec-sum-toolbar-btn__box-icon--plus"
            }`}
            aria-hidden="true"
        />
    );
}

/** Shared bulk expand/collapse logic for executive summary band trees. */
export function useExecutiveSummaryBulkBandsToggle(bands, expandedBandKeys, onExpandBands, onCollapseBands) {
    const bulkBandsBusyRef = useRef(false);
    const [bulkBandsBusy, setBulkBandsBusy] = useState(false);
    const allBandRowKeys = useMemo(() => collectExecutiveSummaryRowKeys(bands), [bands]);
    const hasBands = allBandRowKeys.length > 0;
    const anyBandExpanded = hasBands && allBandRowKeys.some(rowKey => !!expandedBandKeys[rowKey]);

    const applyBandRowKeys = useCallback(
        (rowKeys, expand) => {
            const apply = expand ? onExpandBands : onCollapseBands;
            if (!Array.isArray(rowKeys) || !rowKeys.length || typeof apply !== "function") return;

            if (!expand || rowKeys.length < EXEC_SUM_BULK_EXPAND_LOADER_MIN) {
                apply(rowKeys);
                return;
            }

            bulkBandsBusyRef.current = true;
            setBulkBandsBusy(true);
            let index = 0;

            const runBatch = () => {
                const chunk = rowKeys.slice(index, index + EXEC_SUM_BULK_EXPAND_BATCH);
                if (!chunk.length) {
                    bulkBandsBusyRef.current = false;
                    setBulkBandsBusy(false);
                    return;
                }
                apply(chunk);
                index += EXEC_SUM_BULK_EXPAND_BATCH;
                if (index < rowKeys.length) {
                    window.requestAnimationFrame(runBatch);
                } else {
                    window.requestAnimationFrame(() => {
                        bulkBandsBusyRef.current = false;
                        setBulkBandsBusy(false);
                    });
                }
            };

            window.requestAnimationFrame(runBatch);
        },
        [onCollapseBands, onExpandBands]
    );

    useEffect(
        () => () => {
            bulkBandsBusyRef.current = false;
        },
        []
    );

    const toggleAllBands = useCallback(() => {
        if (!hasBands || bulkBandsBusyRef.current) return;
        if (anyBandExpanded) {
            applyBandRowKeys(
                allBandRowKeys.filter(rowKey => !!expandedBandKeys[rowKey]),
                false
            );
        } else {
            applyBandRowKeys(
                allBandRowKeys.filter(rowKey => !expandedBandKeys[rowKey]),
                true
            );
        }
    }, [allBandRowKeys, anyBandExpanded, applyBandRowKeys, expandedBandKeys, hasBands]);

    const toggleBandRowKeys = useCallback(
        (rowKeys, collapse) => {
            if (!Array.isArray(rowKeys) || !rowKeys.length || bulkBandsBusyRef.current) return;
            const targets = rowKeys.filter(rowKey =>
                collapse ? !!expandedBandKeys[rowKey] : !expandedBandKeys[rowKey]
            );
            if (!targets.length) return;
            applyBandRowKeys(targets, !collapse);
        },
        [applyBandRowKeys, expandedBandKeys]
    );

    return {
        allBandRowKeys,
        hasBands,
        anyBandExpanded,
        bulkBandsBusy,
        toggleAllBands,
        toggleBandRowKeys,
        applyBandRowKeys
    };
}

export function ExecutiveSummaryBulkBandsToolbar({
    bands,
    expandedBandKeys,
    onExpandBands,
    onCollapseBands,
    tooltipIdPrefix = "",
    wrapperClassName,
    includeTooltip = true
}) {
    const { hasBands, anyBandExpanded, bulkBandsBusy, toggleAllBands } = useExecutiveSummaryBulkBandsToggle(
        bands,
        expandedBandKeys,
        onExpandBands,
        onCollapseBands
    );
    const bulkExpandBandsTooltipId = tooltipIdPrefix
        ? `${tooltipIdPrefix}_exec_sum_bulk_expand_bands`
        : "exec_sum_bulk_expand_bands";

    if (!hasBands) return null;

    const button = (
        <button
            type="button"
            className="exec-sum-toolbar-btn exec-sum-toolbar-btn--ghost"
            disabled={bulkBandsBusy}
            aria-label={anyBandExpanded ? EXEC_SUM_BULK_COLLAPSE_BANDS_TIP : EXEC_SUM_BULK_EXPAND_BANDS_TIP}
            data-tip={
                bulkBandsBusy
                    ? "Expanding bands…"
                    : anyBandExpanded
                      ? EXEC_SUM_BULK_COLLAPSE_BANDS_TIP
                      : EXEC_SUM_BULK_EXPAND_BANDS_TIP
            }
            data-for={bulkExpandBandsTooltipId}
            onClick={toggleAllBands}
        >
            {bulkBandsBusy ? (
                <span
                    className="spinner-border spinner-border-sm exec-sum-toolbar-btn__fa-icon"
                    role="status"
                    aria-hidden="true"
                />
            ) : (
                renderExpandCollapseIcon(anyBandExpanded)
            )}
            {bulkBandsBusy ? "Expanding…" : anyBandExpanded ? "Collapse bands" : "Expand bands"}
        </button>
    );

    const content = (
        <>
            {button}
            {includeTooltip ? (
                <ReactTooltip
                    id={bulkExpandBandsTooltipId}
                    place="top"
                    className="rc-tooltip-custom-class"
                    effect="solid"
                    backgroundColor="#007bff"
                />
            ) : null}
        </>
    );

    if (wrapperClassName) {
        return <div className={wrapperClassName}>{content}</div>;
    }

    return content;
}
